<template>
  <div class="copilot-sidebar">
    <header class="copilot-sidebar__header">
      <div class="copilot-sidebar__header-main">
        <h2 class="copilot-sidebar__title">
          {{ $t('copilot.panelTitle') }}
        </h2>
        <span
          class="copilot-sidebar__status"
          :class="`copilot-sidebar__status--${status}`"
        >
          {{ statusLabel }}
        </span>
      </div>
      <button
        type="button"
        class="copilot-sidebar__close"
        :aria-label="$t('copilot.close')"
        @click="closePanel"
      >
        <i class="iconoir-cancel" />
      </button>
    </header>

    <section class="copilot-sidebar__conversation">
      <CopilotMessageList
        :messages="messages"
        :is-thinking="isBusy"
        @load-older-messages="$emit('load-older-messages')"
      />
    </section>

    <footer class="copilot-sidebar__footer">
      <CopilotClarification
        v-if="pendingClarification"
        :question="pendingClarification.question"
        :candidates="pendingClarification.candidates"
        :disabled="isBusy"
        @choose="handleChooseClarification"
      />
      <CopilotNudges
        v-if="showSuggestedQuestions"
        :nudges="suggestedQuestions"
        @accept="handleAcceptNudge"
      />
      <CopilotInput
        :disabled="isBusy"
        @submit="handleSubmit"
      />
    </footer>
  </div>
</template>

<script>
import CopilotMessageList from '@baserow/modules/copilot/components/chat/CopilotMessageList'
import CopilotInput from '@baserow/modules/copilot/components/CopilotInput'
import CopilotNudges from '@baserow/modules/copilot/components/CopilotNudges'
import CopilotClarification from '@baserow/modules/copilot/components/CopilotClarification'

export default {
  name: 'CopilotSidebar',
  components: {
    CopilotMessageList,
    CopilotInput,
    CopilotNudges,
    CopilotClarification,
  },
  props: {
    /**
     * The current workspace object provided by the RightSidebar container.
     * The concrete shape is defined by the core `workspace` store. We do
     * not rely on any specific fields yet to keep this container generic.
     */
    workspace: {
      type: Object,
      required: false,
      default: null,
    },
  },
  computed: {
    messages() {
      return this.$store.getters['copilot/activeMessages']
    },
    status() {
      return this.$store.state.copilot.status
    },
    nudges() {
      return this.$store.state.copilot.nudges
    },
    suggestedQuestions() {
      if (this.nudges.length) {
        return this.nudges
      }
      return [
        {
          id: 'next-deadline',
          label: this.$t('copilot.nudgeNextDeadline'),
          prompt: this.$t('copilot.nudgeNextDeadlinePrompt'),
        },
        {
          id: 'client-count',
          label: this.$t('copilot.nudgeClientCount'),
          prompt: this.$t('copilot.nudgeClientCountPrompt'),
        },
        {
          id: 'upcoming-30',
          label: this.$t('copilot.nudgeUpcoming30'),
          prompt: this.$t('copilot.nudgeUpcoming30Prompt'),
        },
      ]
    },
    showSuggestedQuestions() {
      return this.messages.length === 0 && !this.pendingClarification
    },
    pendingClarification() {
      return this.$store.state.copilot.pendingClarification
    },
    isBusy() {
      return this.status === 'loading' || this.status === 'streaming'
    },
    statusLabel() {
      if (this.status === 'streaming') {
        return this.$t('copilot.statusStreaming')
      }
      if (this.status === 'loading') {
        return this.$t('copilot.statusLoading')
      }
      if (this.status === 'error') {
        return this.$t('copilot.statusError')
      }
      return this.$t('copilot.statusOnline')
    },
  },
  methods: {
    /**
     * Emits the `submit` event with the user prompt so that the parent
     * container can delegate to the Vuex store and service layer.
     */
    handleSubmit(prompt) {
      this.$store.dispatch('copilot/sendUserMessage', { content: prompt })
    },
    handleAcceptNudge(nudge) {
      this.$store.dispatch('copilot/acceptNudge', nudge)
    },
    handleChooseClarification(candidate) {
      this.$store.dispatch('copilot/chooseClarification', candidate)
    },
    closePanel() {
      this.$bus.$emit('toggle-right-sidebar', false)
    },
  },
}
</script>

<style scoped lang="scss">
@import '../../core/assets/scss/colors';

// The Copilot chat uses an intentionally light panel inside the dark sidebar
// to maximize readability for long-form conversations.
.copilot-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 12px 8px;
  box-sizing: border-box;
  background-color: $white;
  color: $palette-neutral-1300;
  border-left: 1px solid rgba(0, 0, 0, 0.06);
}

.copilot-sidebar__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.copilot-sidebar__header-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.copilot-sidebar__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: $palette-neutral-700;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: $palette-neutral-100;
    color: $palette-neutral-1300;
  }

  i {
    font-size: 16px;
  }
}

.copilot-sidebar__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.copilot-sidebar__status {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #4caf50;
}

.copilot-sidebar__status--loading,
.copilot-sidebar__status--streaming {
  color: #b45309;
  animation: copilot-status-pulse 1.4s ease-in-out infinite;
}

.copilot-sidebar__status--error {
  color: #b00020;
}

@keyframes copilot-status-pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.55;
  }
}

.copilot-sidebar__conversation {
  flex: 1;
  min-height: 0;
  margin-bottom: 8px;
}

.copilot-sidebar__footer {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 6px;
}
</style>


